import { useState, useEffect, useContext, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import StyledButton from "../../shared_components/StyledButton/StyledButton";
import AnimationGrid from "./AnimationGrid/AnimationGrid";
import AnimationService from "../../services/AnimationService";
import UserData from "../../services/UserData";
import Permissions from "../../services/Permissions";
import AIAnimationService from "../../services/AIAnimationService";
import { useDebouncedCallback } from "./hooks/useDebouncedCallback";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock } from "@fortawesome/free-solid-svg-icons";
import styles from "./AiAnimationGeneratorHome.module.css";

const PAGE_SIZE = 6;
const SEARCH_DEBOUNCE_MS = 400;

function AiAnimationGeneratorHome() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [filteredAnimations, setFilteredAnimations] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const listRequestId = useRef(0);

  const userData = useContext(UserData);
  const isCreateAllowed = !!(userData && Permissions.isAnyOfTheRolesAllowed(userData, AIAnimationService.createRoles));

  const nextRequestId = useCallback(() => {
    listRequestId.current += 1;
    return listRequestId.current;
  }, []);

  const performSearchRequest = useCallback(
    (searchTerm) => {
      if (!userData) return;
      setSearchQuery(searchTerm);
      setCurrentPage(1);
      setLoading(true);
      const id = nextRequestId();
      Promise.all([
        AnimationService.searchAnimations(
          userData.jwtToken,
          userData,
          searchTerm,
          0,
          PAGE_SIZE
        ),
        AnimationService.getSearchTotalPages(
          userData.jwtToken,
          userData,
          searchTerm,
          PAGE_SIZE
        ),
      ])
        .then(([searchResults, pages]) => {
          if (id !== listRequestId.current) return;
          setFilteredAnimations(searchResults.content || []);
          setTotalPages(pages);
        })
        .catch((error) => {
          console.error("Search error:", error);
        })
        .finally(() => {
          if (id === listRequestId.current) {
            setLoading(false);
          }
        });
    },
    [userData, nextRequestId]
  );

  const [debouncedSearch, cancelDebouncedSearch] = useDebouncedCallback(
    performSearchRequest,
    SEARCH_DEBOUNCE_MS
  );

  const loadListData = useCallback(
    (page = 1, { withTotal = false } = {}) => {
      if (!userData) return;
      setLoading(true);
      const id = nextRequestId();

      if (withTotal) {
        Promise.all([
          AnimationService.getAllAnimations(
            userData.jwtToken,
            userData,
            page - 1,
            PAGE_SIZE
          ),
          AnimationService.getTotalPages(
            userData.jwtToken,
            userData,
            PAGE_SIZE
          ),
        ])
          .then(([response, pages]) => {
            if (id !== listRequestId.current) return;
            setFilteredAnimations(response.content || []);
            setCurrentPage(page);
            setTotalPages(pages);
          })
          .catch((error) => {
            console.error("Error fetching animations:", error);
          })
          .finally(() => {
            if (id === listRequestId.current) {
              setLoading(false);
            }
          });
      } else {
        AnimationService.getAllAnimations(
            userData.jwtToken,
            userData,
            page - 1,
            PAGE_SIZE
          )
          .then((response) => {
            if (id !== listRequestId.current) return;
            setFilteredAnimations(response.content || []);
            setCurrentPage(page);
          })
          .catch((error) => {
            console.error("Error fetching animations:", error);
          })
          .finally(() => {
            if (id === listRequestId.current) {
              setLoading(false);
            }
          });
      }
    },
    [userData, nextRequestId]
  );

  useEffect(() => {
    if (!userData) {
      return;
    }
    loadListData(1, { withTotal: true });
  }, [userData, loadListData]);

  const handleSearchSubmit = (searchTerm) => {
    if (searchTerm === "") {
      return;
    }
    debouncedSearch(searchTerm);
  };

  const handleClearSearch = () => {
    cancelDebouncedSearch();
    setSearchQuery("");
    loadListData(1, { withTotal: true });
  };

  const handlePageChange = (newPage) => {
    if (!userData) return;
    if (searchQuery) {
      setLoading(true);
      const id = nextRequestId();
      AnimationService.searchAnimations(
          userData.jwtToken,
          userData,
          searchQuery,
          newPage - 1,
          PAGE_SIZE
        )
        .then((searchResults) => {
          if (id !== listRequestId.current) return;
          setFilteredAnimations(searchResults.content || []);
          setCurrentPage(newPage);
        })
        .catch((error) => {
          console.error("Search pagination error:", error);
        })
        .finally(() => {
          if (id === listRequestId.current) {
            setLoading(false);
          }
        });
    } else {
      loadListData(newPage, { withTotal: false });
    }
  };

  return (
    <div>
      <div className={styles.centeredButtonContainer}>
        <StyledButton
          className={styles.centeredButton}
          onClick={() => navigate("/animations/generate")}
          disabled={!isCreateAllowed}
          title={!isCreateAllowed ? "You do not have permission to generate animations." : undefined}
        >
          {!isCreateAllowed && <FontAwesomeIcon icon={faLock} style={{ marginRight: '8px' }} />}
          Generate Animation
        </StyledButton>
      </div>

      <AnimationGrid
        handleSearchSubmit={handleSearchSubmit}
        handleClearSearch={handleClearSearch}
        filteredAnimations={filteredAnimations}
        loading={loading}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
      />
    </div>
  );
}

export default AiAnimationGeneratorHome;
